import React, { useEffect, useRef, useState } from 'react';
import { KpiCounts } from '../types';

type KpiFilterKey = 'active' | 'queued' | 'completed' | 'onHold';

interface KpiBarProps {
  counts: KpiCounts;
  activeFilter: KpiFilterKey | null;
  onFilterChange: (filter: KpiFilterKey | null) => void;
  totalProjects: number;
}

/** Animates a number from 0 to `target` over `duration` ms. */
const useCountUp = (target: number, duration = 600): number => {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) {
      setValue(0);
      return;
    }
    startRef.current = null;

    const step = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
};

interface KpiCardProps {
  label: string;
  sublabel: string;
  count: number;
  variant: 'kpi-active' | 'kpi-queued' | 'kpi-complete' | 'kpi-on-hold';
  filterKey: KpiFilterKey;
  isActive: boolean;
  onClick: () => void;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, sublabel, count, variant, isActive, onClick }) => {
  const animatedCount = useCountUp(count, 700);

  const classNames = ['kpi-card', variant, isActive ? 'active' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classNames} onClick={onClick} aria-pressed={isActive} type="button">
      <span className="kpi-dot" aria-hidden="true" />
      <div className="kpi-card-text">
        <span className="kpi-count">{animatedCount}</span>
        <span className="kpi-label">{label}</span>
        <span className="kpi-sublabel">{sublabel}</span>
      </div>
    </button>
  );
};

export const KpiBar: React.FC<KpiBarProps> = ({ counts, activeFilter, onFilterChange, totalProjects }) => {
  const handleClick = (key: KpiFilterKey) => {
    onFilterChange(activeFilter === key ? null : key);
  };

  return (
    <div className="kpi-bar" role="region" aria-label="Project summary">
      <span className="kpi-bar-title">Roadmap</span>
      <div className="kpi-cards">
        <KpiCard
          label="Active"
          sublabel="In Progress · Blocked · Delayed"
          count={counts.active}
          variant="kpi-active"
          filterKey="active"
          isActive={activeFilter === 'active'}
          onClick={() => handleClick('active')}
        />
        <KpiCard
          label="Queued"
          sublabel="Not Started · In Review"
          count={counts.queued}
          variant="kpi-queued"
          filterKey="queued"
          isActive={activeFilter === 'queued'}
          onClick={() => handleClick('queued')}
        />
        <KpiCard
          label="Completed"
          sublabel="Completed · Cancelled"
          count={counts.completed}
          variant="kpi-complete"
          filterKey="completed"
          isActive={activeFilter === 'completed'}
          onClick={() => handleClick('completed')}
        />
        <KpiCard
          label="On Hold"
          sublabel="Paused or not yet committed"
          count={counts.onHold}
          variant="kpi-on-hold"
          filterKey="onHold"
          isActive={activeFilter === 'onHold'}
          onClick={() => handleClick('onHold')}
        />
      </div>
    </div>
  );
};

export default KpiBar;